import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi, eventsApi, authApi, teamsApi, tournamentApi, invoiceApi, meApi, assetUrl } from '../utils/api';
import { IconUsers, IconFish, IconBroadcast, IconDollar, IconCheckCircle, IconFile } from '../components/Icons';
import { eventStatusLabel } from '../utils/eventStatus';

export default function Dashboard() {
  const { user, isAdmin, isOrganizer, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyDismissed, setVerifyDismissed] = useState(false);

  useEffect(() => {
    // Refresh user from server on mount so is_verified reflects latest DB state
    // (the stored token/user may be stale if the user just clicked a verification link)
    if (user && !user.is_verified) {
      refreshUser().catch(() => {});
    }
    fetchData();
  }, []); // eslint-disable-line

  async function fetchData() {
    try {
      if (isAdmin) {
        const { data: dash } = await adminApi.getDashboard();
        setData(dash.data);
      } else if (isOrganizer) {
        const { data: events } = await eventsApi.getEvents();
        setData({ events: events.data });
      } else {
        const { data: events } = await eventsApi.getEvents();
        setData({ events: events.data });
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="spinner" />;

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header page-header-row">
          <div>
            <h1>Dashboard</h1>
            <p>Welcome back, {user.first_name}</p>
          </div>
          {isOrganizer && (
            <Link to="/events/create" className="btn btn-primary">+ New Event</Link>
          )}
        </div>

        {user && !user.is_verified && !verifyDismissed && (
          <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <span>Please check your email to verify your account.</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setVerifyDismissed(true)} style={{ padding: '4px 12px', minWidth: 'auto' }}>Dismiss</button>
          </div>
        )}

        {isAdmin ? <AdminDashboard data={data} /> : isOrganizer ? <OrganizerDashboard data={data} /> : <ParticipantDashboard data={data} />}
      </div>
    </div>
  );
}

// ─── ADMIN DASHBOARD ─────────────────────────────────────────
function AdminDashboard({ data }) {
  const [deleting, setDeleting] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editTournaments, setEditTournaments] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  async function handleDelete(eventId, eventName, e) {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm(`Delete "${eventName}"? This will permanently delete all tournaments, catches, registrations, teams, and sponsors. This cannot be undone.`)) {
      return;
    }

    setDeleting(eventId);
    try {
      await eventsApi.deleteEvent(eventId);
      alert(`Event "${eventName}" deleted successfully`);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete event');
    } finally {
      setDeleting(null);
    }
  }

  function toLocalDatetime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  }

  async function handleEdit(ev, e) {
    e.preventDefault();
    e.stopPropagation();
    setEditError('');
    // Fetch full event data so we don't wipe fields missing from summary responses
    let full = ev;
    try {
      const res = await eventsApi.getEvent(ev.id);
      if (res.data.success) full = res.data.data;
    } catch { /* fall back to summary data */ }
    setEditForm({
      name: full.name || '',
      description: full.description || '',
      location: full.location || '',
      start_date: toLocalDatetime(full.start_date),
      end_date: toLocalDatetime(full.end_date),
      waiver_text: full.waiver_text || '',
      terms_and_conditions: full.terms_and_conditions || '',
      event_type: full.event_type || 'team',
    });
    setEditImageUrl(full.featured_image_url || null);
    setEditingId(ev.id);
    try {
      const res = await tournamentApi.getTournaments(ev.id);
      setEditTournaments(res.data.data || []);
    } catch {
      setEditTournaments([]);
    }
  }

  async function handleImageUpload(file) {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    if (!allowed.includes(file.type)) { setEditError('Image must be JPG, PNG, WebP, or AVIF'); return; }
    if (file.size > 4 * 1024 * 1024) { setEditError('Image must be under 4MB'); return; }
    setImageUploading(true);
    setEditError('');
    try {
      const fd = new FormData();
      fd.append('featured_image', file);
      const res = await eventsApi.uploadFeaturedImage(editingId, fd);
      setEditImageUrl(res.data.data.featured_image_url);
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  }

  async function handleImageRemove() {
    if (!window.confirm('Remove featured image?')) return;
    setImageUploading(true);
    try {
      await eventsApi.deleteFeaturedImage(editingId);
      setEditImageUrl(null);
    } catch {
      setEditError('Failed to remove image');
    } finally {
      setImageUploading(false);
    }
  }

  async function handleEditSave(e) {
    e.preventDefault();
    if (!editForm.name?.trim()) {
      setEditError('Event name is required');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      await eventsApi.updateEvent(editingId, editForm);
      window.location.reload();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update event');
    } finally {
      setEditSaving(false);
    }
  }

  function handleEditCancel(e) {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(null);
    setEditError('');
  }

  if (!data) return null;

  const formatBlocked = editTournaments.length > 0 && editForm.event_type === 'team';
  const inputStyle = { width: '100%', padding: '8px 12px', background: '#152232', border: '1px solid #2a3f56', borderRadius: 6, color: '#f0f2f5', fontSize: '0.85rem' };
  const labelStyle = { display: 'block', color: '#8a9bb5', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 };

  const stats = [
    { label: 'Total Organizers', value: data.total_organizers, color: '#4a9eff', icon: <IconUsers size="22px" /> },
    { label: 'Total Events', value: data.total_events, color: '#e8a838', icon: <IconFish size="22px" /> },
    { label: 'Active Events', value: data.active_events, color: '#3adc8a', icon: <IconBroadcast size="22px" /> },
    { label: 'Total Revenue', value: `$${data.total_revenue_cad.toFixed(2)}`, color: '#e8a838', icon: <IconDollar size="22px" />, isCAD: true },
  ];

  return (
    <>
      <div className="stats-grid">
        {stats.map(stat => (
          <div key={stat.label} className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: '#8a9bb5', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{stat.label}</div>
                <div style={{ fontSize: '1.6rem', fontFamily: 'Oswald, sans-serif', fontWeight: 600, color: stat.color }}>
                  {stat.value} {stat.isCAD && <span style={{ fontSize: '0.7rem', color: '#8a9bb5' }}>CAD</span>}
                </div>
              </div>
              <span style={{ color: stat.color, opacity: 0.8 }}>{stat.icon}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Events */}
      <div className="card">
        <div className="card-header">
          <h3>Recent Events</h3>
          <Link to="/admin/events" style={{ fontSize: '0.82rem', color: '#e8a838' }}>View All →</Link>
        </div>
        <div className="table-wrap">
          <table className="responsive-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Organizer</th>
                <th>Format</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Revenue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_events?.map(ev => (
                <React.Fragment key={ev.id}>
                  <tr>
                    <td style={{ fontWeight: 500 }}>{ev.name}</td>
                    <td data-label="Organizer" style={{ color: '#8a9bb5' }}>{ev.organizer_name}</td>
                    <td data-label="Format">
                      <span style={{ background: '#e8a838', color: '#0a1628', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                        {ev.event_type === 'individual' ? 'Participant' : 'Team'}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className={`badge badge-${ev.status}`}>{eventStatusLabel(ev.status)}</span>
                    </td>
                    <td data-label="Payment"><span className={`badge badge-${ev.payment_status}`}>{ev.payment_status}</span></td>
                    <td data-label="Revenue" style={{ color: '#e8a838' }}>${parseFloat(ev.amount_paid || 0).toFixed(2)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#e8a838' }}
                          onClick={(e) => handleEdit(ev, e)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#e85d5d' }}
                          onClick={(e) => handleDelete(ev.id, ev.name, e)}
                          disabled={deleting === ev.id}
                        >
                          {deleting === ev.id ? '...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {editingId === ev.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <form onSubmit={handleEditSave} onClick={e => e.stopPropagation()} style={{
                          background: '#1a2a3a', padding: 20,
                          display: 'flex', flexDirection: 'column', gap: 14
                        }}>
                          {editError && (
                            <div className="alert alert-danger" style={{ margin: 0, padding: '8px 14px', fontSize: '0.85rem' }}>{editError}</div>
                          )}

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <div>
                              <label style={labelStyle}>Event Name *</label>
                              <input style={inputStyle} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                            </div>
                            <div>
                              <label style={labelStyle}>Location</label>
                              <input style={inputStyle} value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} />
                            </div>
                          </div>

                          <div>
                            <label style={labelStyle}>Description</label>
                            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <div>
                              <label style={labelStyle}>Start Date</label>
                              <input type="datetime-local" style={inputStyle} value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} />
                            </div>
                            <div>
                              <label style={labelStyle}>End Date</label>
                              <input type="datetime-local" style={inputStyle} value={editForm.end_date} onChange={e => setEditForm(f => ({ ...f, end_date: e.target.value }))} />
                            </div>
                          </div>

                          <div>
                            <label style={labelStyle}>Event Waiver</label>
                            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.waiver_text} onChange={e => setEditForm(f => ({ ...f, waiver_text: e.target.value }))} />
                          </div>

                          <div>
                            <label style={labelStyle}>Terms & Conditions</label>
                            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.terms_and_conditions} onChange={e => setEditForm(f => ({ ...f, terms_and_conditions: e.target.value }))} />
                          </div>

                          <div>
                            <label style={labelStyle}>Event Format</label>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <button type="button" style={{
                                padding: '6px 16px', borderRadius: 6, fontSize: '0.85rem', cursor: formatBlocked ? 'not-allowed' : 'pointer',
                                background: editForm.event_type === 'team' ? '#e8a838' : '#152232',
                                color: editForm.event_type === 'team' ? '#0a1628' : '#8a9bb5',
                                border: editForm.event_type === 'team' ? 'none' : '1px solid #2a3f56',
                                opacity: formatBlocked && editForm.event_type !== 'team' ? 0.5 : 1
                              }} disabled={formatBlocked} onClick={() => setEditForm(f => ({ ...f, event_type: 'team' }))}>Team</button>
                              <button type="button" style={{
                                padding: '6px 16px', borderRadius: 6, fontSize: '0.85rem', cursor: formatBlocked ? 'not-allowed' : 'pointer',
                                background: editForm.event_type === 'individual' ? '#e8a838' : '#152232',
                                color: editForm.event_type === 'individual' ? '#0a1628' : '#8a9bb5',
                                border: editForm.event_type === 'individual' ? 'none' : '1px solid #2a3f56',
                                opacity: formatBlocked && editForm.event_type !== 'individual' ? 0.5 : 1
                              }} disabled={formatBlocked} onClick={() => setEditForm(f => ({ ...f, event_type: 'individual' }))}>Individual</button>
                            </div>
                            {formatBlocked && (
                              <div style={{ color: '#e85d5d', fontSize: '0.75rem', marginTop: 6 }}>
                                Delete existing tournaments before changing event format.
                              </div>
                            )}
                          </div>

                          <div>
                            <label style={labelStyle}>Featured Image</label>
                            {editImageUrl ? (
                              <div style={{ position: 'relative' }}>
                                <img src={assetUrl(editImageUrl)} alt="Featured" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid #2a3f56' }} />
                                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                  <label style={{ color: '#4a9eff', fontSize: '0.8rem', cursor: imageUploading ? 'default' : 'pointer', opacity: imageUploading ? 0.5 : 1 }}>
                                    {imageUploading ? 'Uploading...' : 'Replace'}
                                    <input type="file" accept=".jpg,.jpeg,.png,.webp,.avif" onChange={e => handleImageUpload(e.target.files[0])} disabled={imageUploading} style={{ display: 'none' }} />
                                  </label>
                                  <button type="button" onClick={handleImageRemove} disabled={imageUploading} style={{ background: 'none', border: 'none', color: '#e85d5d', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>Remove</button>
                                </div>
                              </div>
                            ) : (
                              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '16px 12px', border: '2px dashed #2a3f56', borderRadius: 8, cursor: imageUploading ? 'default' : 'pointer', color: '#8a9bb5', fontSize: '0.82rem', opacity: imageUploading ? 0.5 : 1 }}>
                                {imageUploading ? 'Uploading...' : 'Click to upload image (JPG, PNG, WebP, AVIF — max 4MB)'}
                                <input type="file" accept=".jpg,.jpeg,.png,.webp,.avif" onChange={e => handleImageUpload(e.target.files[0])} disabled={imageUploading} style={{ display: 'none' }} />
                              </label>
                            )}
                          </div>

                          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={handleEditCancel} style={{ padding: '6px 16px' }}>Cancel</button>
                            <button type="submit" className="btn btn-primary btn-sm" disabled={editSaving} style={{ padding: '6px 16px' }}>
                              {editSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── ORGANIZER DASHBOARD ─────────────────────────────────────
function OrganizerDashboard({ data }) {
  const events = data?.events || [];
  const [deleting, setDeleting] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editTournaments, setEditTournaments] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    teamsApi.pendingRequests().then(res => setPendingCount(res.data.data?.length || 0)).catch(() => {});
  }, []);

  async function handleDelete(eventId, eventName, e) {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm(`Delete "${eventName}"? This will permanently delete all tournaments, catches, registrations, teams, and sponsors. This cannot be undone.`)) {
      return;
    }

    setDeleting(eventId);
    try {
      await eventsApi.deleteEvent(eventId);
      alert(`Event "${eventName}" deleted successfully`);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete event');
    } finally {
      setDeleting(null);
    }
  }

  function toLocalDatetime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  }

  async function handleEdit(ev, e) {
    e.preventDefault();
    e.stopPropagation();
    setEditError('');
    // Fetch full event data so we don't wipe fields missing from summary responses
    let full = ev;
    try {
      const res = await eventsApi.getEvent(ev.id);
      if (res.data.success) full = res.data.data;
    } catch { /* fall back to summary data */ }
    setEditForm({
      name: full.name || '',
      description: full.description || '',
      location: full.location || '',
      start_date: toLocalDatetime(full.start_date),
      end_date: toLocalDatetime(full.end_date),
      waiver_text: full.waiver_text || '',
      terms_and_conditions: full.terms_and_conditions || '',
      event_type: full.event_type || 'team',
    });
    setEditImageUrl(full.featured_image_url || null);
    setEditingId(ev.id);
    try {
      const res = await tournamentApi.getTournaments(ev.id);
      setEditTournaments(res.data.data || []);
    } catch {
      setEditTournaments([]);
    }
  }

  async function handleImageUpload(file) {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    if (!allowed.includes(file.type)) { setEditError('Image must be JPG, PNG, WebP, or AVIF'); return; }
    if (file.size > 4 * 1024 * 1024) { setEditError('Image must be under 4MB'); return; }
    setImageUploading(true);
    setEditError('');
    try {
      const fd = new FormData();
      fd.append('featured_image', file);
      const res = await eventsApi.uploadFeaturedImage(editingId, fd);
      setEditImageUrl(res.data.data.featured_image_url);
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  }

  async function handleImageRemove() {
    if (!window.confirm('Remove featured image?')) return;
    setImageUploading(true);
    try {
      await eventsApi.deleteFeaturedImage(editingId);
      setEditImageUrl(null);
    } catch {
      setEditError('Failed to remove image');
    } finally {
      setImageUploading(false);
    }
  }

  async function handleEditSave(e) {
    e.preventDefault();
    if (!editForm.name?.trim()) {
      setEditError('Event name is required');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      await eventsApi.updateEvent(editingId, editForm);
      window.location.reload();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update event');
    } finally {
      setEditSaving(false);
    }
  }

  function handleEditCancel(e) {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(null);
    setEditError('');
  }

  const formatBlocked = editTournaments.length > 0 && editForm.event_type === 'team';
  const inputStyle = { width: '100%', padding: '8px 12px', background: '#152232', border: '1px solid #2a3f56', borderRadius: 6, color: '#f0f2f5', fontSize: '0.85rem' };
  const labelStyle = { display: 'block', color: '#8a9bb5', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 };

  return (
    <>
      {pendingCount > 0 && (
        <Link to="/teams" className="card" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', marginBottom: 20, textDecoration: 'none',
          border: '1px solid #e8a838', background: 'rgba(232,168,56,0.08)'
        }}>
          <span style={{ color: '#f0f2f5', fontWeight: 500 }}>
            You have {pendingCount} pending join request{pendingCount !== 1 ? 's' : ''} to review
          </span>
          <span style={{ color: '#e8a838', fontSize: '0.85rem' }}>Go to Teams →</span>
        </Link>
      )}

      <div className="stats-grid">
        <StatCard label="Total Events" value={events.length} icon={<IconFish size="22px" />} color="#e8a838" />
        <StatCard label="Active Events" value={events.filter(e => ['active','live'].includes(e.status)).length} icon={<IconBroadcast size="22px" />} color="#3adc8a" />
        <StatCard label="Completed" value={events.filter(e => e.status === 'completed').length} icon={<IconCheckCircle size="22px" />} color="#4a9eff" />
      </div>

      <div className="card">
        <div className="card-header">
          <h3>My Events</h3>
        </div>
        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8a9bb5' }}>
            <p style={{ fontSize: '1.1rem', marginBottom: 12 }}>No events yet</p>
            <Link to="/events/create" className="btn btn-primary">Create Your First Derby</Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {events.map(ev => (
              <div key={ev.id}>
                <Link to={`/events/${ev.id}`} className="event-card-row" style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 18px', background: '#1e2d3d', borderRadius: editingId === ev.id ? '10px 10px 0 0' : 10,
                  border: '1px solid #2a3f56', textDecoration: 'none',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#e8a838'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3f56'}
                >
                  <div>
                    <div style={{ color: '#f0f2f5', fontWeight: 500, marginBottom: 2 }}>{ev.name}</div>
                    <div style={{ color: '#8a9bb5', fontSize: '0.8rem' }}>
                      {ev.location || 'No location'} · {new Date(ev.start_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ background: '#e8a838', color: '#0a1628', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                      {ev.event_type === 'individual' ? 'Participant' : 'Team'}
                    </span>
                    <span className={`badge badge-${ev.payment_status}`}>{ev.payment_status}</span>
                    {ev.status === 'pending_payment' ? (
                      <button className="btn btn-warning btn-sm" style={{ padding: '3px 12px', fontSize: '0.75rem', fontWeight: 600 }} onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/events/${ev.id}/pay`; }}>
                        Complete Payment
                      </button>
                    ) : (
                      <span className={`badge badge-${ev.status}`}>{eventStatusLabel(ev.status)}</span>
                    )}
                    {ev.payment_status === 'paid' && ev.invoice_id && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#4a9eff' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          invoiceApi.downloadInvoice(ev.invoice_id);
                        }}
                        title="Download Invoice"
                      >
                        <IconFile size="13px" style={{ marginRight: 5, display: 'inline-block', verticalAlign: 'middle' }} />Invoice
                      </button>
                    )}
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#e8a838' }}
                      onClick={(e) => handleEdit(ev, e)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#e85d5d' }}
                      onClick={(e) => handleDelete(ev.id, ev.name, e)}
                      disabled={deleting === ev.id}
                    >
                      {deleting === ev.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </Link>

                {editingId === ev.id && (
                  <form onSubmit={handleEditSave} onClick={e => e.stopPropagation()} style={{
                    background: '#1a2a3a', border: '1px solid #2a3f56', borderTop: 'none',
                    borderRadius: '0 0 10px 10px', padding: 20,
                    display: 'flex', flexDirection: 'column', gap: 14
                  }}>
                    {editError && (
                      <div className="alert alert-danger" style={{ margin: 0, padding: '8px 14px', fontSize: '0.85rem' }}>{editError}</div>
                    )}

                    <div>
                      <label style={labelStyle}>Event Name *</label>
                      <input style={inputStyle} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                    </div>

                    <div>
                      <label style={labelStyle}>Description</label>
                      <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                    </div>

                    <div>
                      <label style={labelStyle}>Location</label>
                      <input style={inputStyle} value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div>
                        <label style={labelStyle}>Start Date</label>
                        <input type="datetime-local" style={inputStyle} value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} />
                      </div>
                      <div>
                        <label style={labelStyle}>End Date</label>
                        <input type="datetime-local" style={inputStyle} value={editForm.end_date} onChange={e => setEditForm(f => ({ ...f, end_date: e.target.value }))} />
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Event Waiver</label>
                      <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.waiver_text} onChange={e => setEditForm(f => ({ ...f, waiver_text: e.target.value }))} />
                    </div>

                    <div>
                      <label style={labelStyle}>Terms & Conditions</label>
                      <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.terms_and_conditions} onChange={e => setEditForm(f => ({ ...f, terms_and_conditions: e.target.value }))} />
                    </div>

                    <div>
                      <label style={labelStyle}>Event Format</label>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <button type="button" style={{
                          padding: '6px 16px', borderRadius: 6, fontSize: '0.85rem', cursor: formatBlocked ? 'not-allowed' : 'pointer',
                          background: editForm.event_type === 'team' ? '#e8a838' : '#152232',
                          color: editForm.event_type === 'team' ? '#0a1628' : '#8a9bb5',
                          border: editForm.event_type === 'team' ? 'none' : '1px solid #2a3f56',
                          opacity: formatBlocked && editForm.event_type !== 'team' ? 0.5 : 1
                        }} disabled={formatBlocked} onClick={() => setEditForm(f => ({ ...f, event_type: 'team' }))}>Team</button>
                        <button type="button" style={{
                          padding: '6px 16px', borderRadius: 6, fontSize: '0.85rem', cursor: formatBlocked ? 'not-allowed' : 'pointer',
                          background: editForm.event_type === 'individual' ? '#e8a838' : '#152232',
                          color: editForm.event_type === 'individual' ? '#0a1628' : '#8a9bb5',
                          border: editForm.event_type === 'individual' ? 'none' : '1px solid #2a3f56',
                          opacity: formatBlocked && editForm.event_type !== 'individual' ? 0.5 : 1
                        }} disabled={formatBlocked} onClick={() => setEditForm(f => ({ ...f, event_type: 'individual' }))}>Individual</button>
                      </div>
                      {formatBlocked && (
                        <div style={{ color: '#e85d5d', fontSize: '0.75rem', marginTop: 6 }}>
                          Delete existing tournaments before changing event format.
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={labelStyle}>Featured Image</label>
                      {editImageUrl ? (
                        <div style={{ position: 'relative' }}>
                          <img src={assetUrl(editImageUrl)} alt="Featured" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid #2a3f56' }} />
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <label style={{ color: '#4a9eff', fontSize: '0.8rem', cursor: imageUploading ? 'default' : 'pointer', opacity: imageUploading ? 0.5 : 1 }}>
                              {imageUploading ? 'Uploading...' : 'Replace'}
                              <input type="file" accept=".jpg,.jpeg,.png,.webp,.avif" onChange={e => handleImageUpload(e.target.files[0])} disabled={imageUploading} style={{ display: 'none' }} />
                            </label>
                            <button type="button" onClick={handleImageRemove} disabled={imageUploading} style={{ background: 'none', border: 'none', color: '#e85d5d', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>Remove</button>
                          </div>
                        </div>
                      ) : (
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '16px 12px', border: '2px dashed #2a3f56', borderRadius: 8, cursor: imageUploading ? 'default' : 'pointer', color: '#8a9bb5', fontSize: '0.82rem', opacity: imageUploading ? 0.5 : 1 }}>
                          {imageUploading ? 'Uploading...' : 'Click to upload image (JPG, PNG, WebP, AVIF — max 4MB)'}
                          <input type="file" accept=".jpg,.jpeg,.png,.webp,.avif" onChange={e => handleImageUpload(e.target.files[0])} disabled={imageUploading} style={{ display: 'none' }} />
                        </label>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={handleEditCancel} style={{ padding: '6px 16px' }}>Cancel</button>
                      <button type="submit" className="btn btn-primary btn-sm" disabled={editSaving} style={{ padding: '6px 16px' }}>
                        {editSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ─── PARTICIPANT DASHBOARD ───────────────────────────────────
function TeamHubCard() {
  const navigate = useNavigate();
  const [teamStatus, setTeamStatus] = useState(null);

  useEffect(() => {
    meApi.getTeamStatus().then(r => setTeamStatus(r.data)).catch(() => {});
  }, []);

  if (!teamStatus) return null;

  if (teamStatus.on_team) {
    const team = teamStatus.team;
    return (
      <div style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1a3c5e 100%)', borderRadius: 14, padding: '24px 28px', marginBottom: 20, color: '#fff', boxShadow: '0 4px 12px rgba(13,27,42,0.15)' }}>
        <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 600, color: '#e8a838', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
          🎣 Your Team
        </div>
        <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 700, marginBottom: 4 }}>
          {team.team_name}
          {team.team_number && <span style={{ color: '#e8a838', fontSize: 18, marginLeft: 10 }}>#{team.team_number}</span>}
        </div>
        {team.event_name && <div style={{ fontSize: 13, color: '#8a9bb5', marginBottom: 14 }}>{team.event_name}</div>}
        <div style={{ display: 'flex', gap: 24, fontSize: 13, color: '#cfd6e0', marginBottom: 16, flexWrap: 'wrap' }}>
          <div>👥 {team.member_count}/{team.max_members} members</div>
          {team.captain_name && <div>⭐ Captain: {team.captain_name}</div>}
          {team.is_captain && <div style={{ color: '#e8a838', fontWeight: 600 }}>You're the captain</div>}
        </div>
        {/* TODO: link to /teams/:id when team profile page is available */}
        <button onClick={() => navigate('/teams?tab=my-teams')} style={{ background: '#e8a838', color: '#0d1b2a', border: 'none', padding: '12px 24px', borderRadius: 8, fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', cursor: 'pointer' }}>
          View Team Profile
        </button>
      </div>
    );
  }

  const summary = teamStatus.browse_summary || {};
  const hasInfo = summary.orphaned_team_count > 0 || summary.open_team_count > 0 || summary.pending_request_count > 0;
  return (
    <div style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fb 100%)', border: '1px solid #e0e3e8', borderRadius: 14, padding: '24px 28px', marginBottom: 20, boxShadow: '0 2px 6px rgba(13,27,42,0.04)' }}>
      <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 600, color: '#8a9bb5', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
        🎣 Join a Team
      </div>
      <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 700, color: '#0d1b2a', marginBottom: 12 }}>
        Are you a captain of a team or looking to join one?
      </div>
      {hasInfo && (
        <div style={{ fontSize: 14, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
          {summary.orphaned_team_count > 0 && <div>• {summary.orphaned_team_count} {summary.orphaned_team_count === 1 ? 'team needs' : 'teams need'} a captain</div>}
          {summary.open_team_count > 0 && <div>• {summary.open_team_count} {summary.open_team_count === 1 ? 'team is' : 'teams are'} accepting members</div>}
          {summary.pending_request_count > 0 && (
            <div style={{ color: '#1565c0', marginTop: 6 }}>
              ⏳ You have {summary.pending_request_count} pending {summary.pending_request_count === 1 ? 'request' : 'requests'} awaiting response
            </div>
          )}
        </div>
      )}
      <button onClick={() => navigate('/teams?tab=browse')} style={{ background: '#0d1b2a', color: '#e8a838', border: 'none', padding: '12px 24px', borderRadius: 8, fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', cursor: 'pointer' }}>
        Browse Teams &amp; Join
      </button>
    </div>
  );
}

function ParticipantDashboard({ data }) {
  const events = data?.events || [];
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    teamsApi.pendingRequests().then(res => setPendingCount(res.data.data?.length || 0)).catch(() => {});
  }, []);

  const now = new Date();

  const activeEvents = events.filter(ev => {
    if (ev.status === 'live') return true;
    if (ev.status === 'active') {
      const start = new Date(ev.start_date);
      const end = new Date(ev.end_date);
      return start <= now && now <= end;
    }
    return false;
  });

  const upcomingEvents = events.filter(ev => {
    if (ev.status !== 'active') return false;
    return new Date(ev.start_date) > now;
  });

  const pastEvents = events.filter(ev => {
    if (ev.status === 'completed') return true;
    if (ev.status === 'active') return new Date(ev.end_date) < now;
    return false;
  });

  const hasEvents = activeEvents.length > 0 || upcomingEvents.length > 0 || pastEvents.length > 0;

  const EventRow = ({ ev }) => (
    <Link to={`/events/${ev.id}/scoreboard`} className="event-card-row" style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '14px 18px', background: '#1e2d3d', borderRadius: 10,
      border: '1px solid #2a3f56', textDecoration: 'none'
    }}>
      <div>
        <div style={{ color: '#f0f2f5', fontWeight: 500 }}>{ev.name}</div>
        <div style={{ color: '#8a9bb5', fontSize: '0.8rem' }}>{ev.location} · {new Date(ev.start_date).toLocaleDateString()}</div>
      </div>
      <span className={`badge badge-${ev.status}`}>{eventStatusLabel(ev.status)}</span>
    </Link>
  );

  return (
    <>
      <TeamHubCard />

      {pendingCount > 0 && (
        <Link to="/teams" className="card" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', marginBottom: 20, textDecoration: 'none',
          border: '1px solid #e8a838', background: 'rgba(232,168,56,0.08)'
        }}>
          <span style={{ color: '#f0f2f5', fontWeight: 500 }}>
            You have {pendingCount} pending join request{pendingCount !== 1 ? 's' : ''} to review
          </span>
          <span style={{ color: '#e8a838', fontSize: '0.85rem' }}>Go to Teams →</span>
        </Link>
      )}

      {!hasEvents ? (
        <div className="card">
          <div className="card-header"><h3>Available Events</h3></div>
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8a9bb5' }}>
            <p>No events available right now. Check back soon!</p>
          </div>
        </div>
      ) : (
        <>
          {activeEvents.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">
                <h3>Active Events</h3>
                <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{activeEvents.length} event{activeEvents.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {activeEvents.map(ev => <EventRow key={ev.id} ev={ev} />)}
              </div>
            </div>
          )}
          {upcomingEvents.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">
                <h3>Upcoming Events</h3>
                <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{upcomingEvents.length} event{upcomingEvents.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {upcomingEvents.map(ev => <EventRow key={ev.id} ev={ev} />)}
              </div>
            </div>
          )}
          {pastEvents.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">
                <h3>Past Events</h3>
                <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{pastEvents.length} event{pastEvents.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pastEvents.map(ev => <EventRow key={ev.id} ev={ev} />)}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ color: '#8a9bb5', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{label}</div>
          <div style={{ fontSize: '1.6rem', fontFamily: 'Oswald, sans-serif', fontWeight: 600, color }}>{value}</div>
        </div>
        <span style={{ color }}>{icon}</span>
      </div>
    </div>
  );
}
